export type NormalizedTransactionErrorCode = 'USER_REJECTED' | 'CONTRACT_REVERTED' | 'NETWORK_ERROR' | 'CONFIRM_TIMEOUT' | 'UNKNOWN';

export interface NormalizedTransactionError {
    code: NormalizedTransactionErrorCode;
    messageKey: string;
    fallbackMessage?: string;
}

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message?: unknown }).message || '');
    }
    return '';
};

const getErrorCode = (error: unknown): number | string | undefined => {
    if (error && typeof error === 'object' && 'code' in error) {
        return (error as { code?: number | string }).code;
    }
    return undefined;
};

const getErrorReason = (error: unknown): string => {
    if (error && typeof error === 'object' && 'reason' in error) {
        return String((error as { reason?: unknown }).reason || '');
    }
    return '';
};

export const normalizeTransactionError = (error: unknown): NormalizedTransactionError => {
    const code = getErrorCode(error);
    const message = getErrorMessage(error);
    const reason = getErrorReason(error);
    const combined = `${message} ${reason}`.toLowerCase();

    if (code === 4001 || combined.includes('user rejected') || combined.includes('user denied') || code === 'ACTION_REJECTED') {
        return { code: 'USER_REJECTED', messageKey: 'error.user-rejected' };
    }

    if (code === 'UNPREDICTABLE_GAS_LIMIT' || combined.includes('execution reverted') || combined.includes('cannot estimate gas')) {
        return {
            code: 'CONTRACT_REVERTED',
            messageKey: 'error.contract-reverted',
        };
    }

    if (combined.includes('ticket_payment_confirm_timeout')) {
        return { code: 'CONFIRM_TIMEOUT', messageKey: 'error.confirm-timeout' };
    }

    if (combined.includes('network') || combined.includes('timeout') || combined.includes('rpc')) {
        return { code: 'NETWORK_ERROR', messageKey: 'error.network' };
    }

    return { code: 'UNKNOWN', messageKey: 'error.unknown', fallbackMessage: message || undefined };
};
